import axios, { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useChatStore } from '../../stores/chat.store'
import AuthButtons from './AuthButtons/AuthButtons'
import styles from './BusinessChat.module.css'

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL

// Сколько времени бездействия для сброса истории чата
const INACTIVITY_TIMEOUT = 60 * 60 * 1000

function BusinessChat() {
	const {
		hasHydrated,
		sessionId,
		accessToken,
		lastBotResponseTime,
		setLastBotResponseTime,
		startStatusCheck,
		setStartStatusCheck,
		vendorRegistrationId,
		setVendorRegistrationId,
		clearStore,
	} = useChatStore()

	const [inputValue, setInputValue] = useState('')
	const [isTyping, setIsTyping] = useState(false)
	const [errorSendMessage, setErrorSendMessage] = useState({
		error: null,
		message: null,
	})
	const [inactivityTimer, setInactivityTimer] = useState(null)
	const [progress, setProgress] = useState(null)

	const [messages, setMessages] = useState([])
	const [chatState, setChatState] = useState({
		actions: null,
		vendorData: null,
		finished: false,
	})

	const [statusTimer, setStatusTimer] = useState(null)

	const textareaRef = useRef(null)
	const messagesEndRef = useRef(null)

	// Скроллим к низу
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	// Устанавливаем таймер на оставшееся время
	const setupInactivityTimer = () => {
		if (inactivityTimer) {
			clearTimeout(inactivityTimer)
		}

		const now = Date.now()
		let timeLeft = INACTIVITY_TIMEOUT // время по умолчанию

		// Если есть сохраненное время последнего ответа, вычисляем оставшееся время
		if (lastBotResponseTime) {
			const elapsed = now - lastBotResponseTime
			timeLeft = Math.max(0, INACTIVITY_TIMEOUT - elapsed)
		}

		// Если время вышло, очищаем чат
		if (timeLeft === 0) {
			handleClear()
			return
		}

		// Устанавливаем новый таймер на оставшееся время
		const timer = setTimeout(() => {
			handleClear()
		}, timeLeft)
		setInactivityTimer(timer)
	}

	// Инициализируем агента
	const initialize = () => {
		setIsTyping(true)
		axios
			.get(
				`${BACKEND_URL}services/vendor/registration-agent/${sessionId}`,
				{
					headers: {
						...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
					},
				}
			)
			.then(({ data }) => {
				if (!data.status) {
					alert(data.error)
					return
				}

				setMessages(data.data.messages)
				setChatState(data.data.state)

				setIsTyping(false)

				// Обновляем время последнего ответа и устанавливаем таймер
				setLastBotResponseTime(Date.now())
				setupInactivityTimer()
			})
			.catch(error => {
				console.error(error)
				if (error instanceof AxiosError && error.response.status === 401) {
					handleClear()
					return
				}
				setTimeout(() => initialize(), 3000)
			})
	}

	useEffect(() => {
		if (!hasHydrated) return
		// Инициализируем агента
		initialize()
		// Устанавливаем таймер при монтировании
		setupInactivityTimer()
		// Если есть ID регистрации, запускаем обновление статуса создания бизнеса
		if (vendorRegistrationId) {
			setStartStatusCheck(true)
		}
		// Очищаем таймер при размонтировании компонента
		return () => {
			if (inactivityTimer) {
				clearTimeout(inactivityTimer)
			}
		}
	}, [hasHydrated, sessionId])

	useEffect(() => {
		console.log('vendorRegistrationId')
		setStartStatusCheck(vendorRegistrationId > 0)
	}, [vendorRegistrationId])

	useEffect(() => {
		scrollToBottom()
	}, [messages, isTyping])

	// Проверяем статус регистрации
	const checkStatus = () => {
		axios
			.get(
				`${BACKEND_URL}services/vendor/registration-agent/${sessionId}/status/${vendorRegistrationId}`,
				{
					headers: {
						...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
					},
				}
			)
			.then(({ data }) => {
				console.log(data)
				if (data) {
					setProgress(data)
					if (data.step === 'error') {
						setStartStatusCheck(false)
						return
					}
					// Если процесс не завершен, продолжаем проверять
					if (data.percent < 100 && data.step !== 'error') {
						const timer = setTimeout(checkStatus, 5000)
						setStatusTimer(timer)
						setStartStatusCheck(true)
					} else {
						setStartStatusCheck(false)
					}
				} else {
					setStartStatusCheck(false)
					setProgress(null)
				}
			})
			.catch(error => {
				console.error(error)
				// В случае ошибки пробуем снова
				const timer = setTimeout(checkStatus, 5000)
				setStatusTimer(timer)
			})
	}

	// Запускаем проверку статуса когда startStatusCheck становится true
	useEffect(() => {
		console.log('startStatusCheck', startStatusCheck)
		if (startStatusCheck) {
			checkStatus()
		}
		return () => {
			if (statusTimer) {
				clearTimeout(statusTimer)
			}
		}
	}, [startStatusCheck])

	// Регулируем высоту textarea
	const adjustTextareaHeight = () => {
		const textarea = textareaRef.current
		if (textarea) {
			textarea.style.height = 'auto'
			textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
		}
	}

	// Регулируем высоту textarea при изменении значения
	useEffect(() => {
		adjustTextareaHeight()
	}, [inputValue])

	const resendLastMessage = async () => {
		setMessages(messages.pop())
		const message = errorSendMessage.message
		setErrorSendMessage({
			error: null,
			message: null,
		})
		await handleSendMessage(message)
	}

	// Отправляем сообщение
	const handleSendMessage = async message => {
		if (!message.trim()) {
			alert('Пожалуйста, введите сообщение')
			return
		}

		setMessages([...messages, { content: message, role: 'user' }])
		setInputValue('')
		setIsTyping(true)
		setErrorSendMessage({
			error: null,
			message: null,
		})

		try {
			const { data } = await axios.post(
				`${BACKEND_URL}services/vendor/registration-agent/${sessionId}`,
				{ message: message },
				{
					headers: {
						...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
					},
				}
			)

			if (!data.status) {
				setErrorSendMessage({
					error: data.error,
					message: message,
				})
				return
			}

			setMessages(prev => [...prev, data.data.message])
			setChatState(data.data.state)

			if (data.data.state.vendorRegistrationId) {
				setVendorRegistrationId(data.data.state.vendorRegistrationId)
				setStartStatusCheck(true)
			}

			// Обновляем время последнего ответа и устанавливаем таймер
			setLastBotResponseTime(Date.now())
			setupInactivityTimer()
		} catch (error) {
			console.error(error)
			setErrorSendMessage({
				error: 'Ошибка при обработке запроса',
				message: message,
			})
		} finally {
			setIsTyping(false)
		}
	}

	// Очищаем чат
	const handleClear = async () => {
		clearStore()
	}

	const handleKeyDown = e => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSendMessage(inputValue)
		}
	}

	const goToCRM = e => {
		handleClear().then(() => {
			location.href = 'https://crm.staging.svoimax.ru'
		})
	}

	const TypingIndicator = () => (
		<div className={styles.typingIndicator}>
			<div className={styles.typingDot}></div>
			<div className={styles.typingDot}></div>
			<div className={styles.typingDot}></div>
		</div>
	)

	if (!hasHydrated) {
		return <></>
	}

	return (
		<div className={styles.container}>
			<h2 className={styles.title}>Бизнес-регистратор</h2>
			<div className={styles.messagesContainer}>
				{messages.map((message, index) =>
					errorSendMessage &&
						errorSendMessage.message &&
						errorSendMessage.message === message.content ? (
						<div key={index} className={`${styles.resendBlock}`}>
							<div className={`${styles.resendMessage}`}>{message.content}</div>
							<div className={`${styles.resendError}`}>
								Ошибка отправки:{' '}
								<span style={{ textDecoration: 'underline' }}>
									{errorSendMessage.error}
								</span>
							</div>
							<div className={`${styles.resendBtnBlock}`}>
								<button onClick={() => resendLastMessage()}>повторить</button>
							</div>
						</div>
					) : (
						<div
							key={index}
							className={`${styles.message} ${message.role === 'user' ? styles.userMessage : styles.aiMessage
								}`}
						>
							{message.content}
						</div>
					)
				)}
				{isTyping && <TypingIndicator />}
				<div ref={messagesEndRef} />
			</div>
			{!progress && (
				<div className={styles.actionsContainer}>
					{!isTyping &&
						chatState.actions?.map(action =>
							action.label === 'Авторизоваться' ? (
								<AuthButtons handleSendMessage={handleSendMessage} />
							) : (
								<button
									className={`${styles.actionButton} ${styles.actionInfoButton}`}
									onClick={() => handleSendMessage(action.label)}
								>
									{action.label}
								</button>
							)
						)}
				</div>
			)}
			{progress && (
				<div className={styles.progressContainer}>
					{progress.step === 'error' && (
						<>
							<div className={styles.errorMessage}>{progress.message}</div>
							<button
								className={`${styles.actionButton} ${styles.actionInfoButton}`}
								onClick={() => handleSendMessage('Создать бизнес')}
							>
								Повторить
							</button>
						</>
					)}
					{progress.step !== 'error' && progress.step !== 'finished' && (
						<div className={styles.currentStepMessage}>{progress.message}</div>
					)}
					{progress.step === 'finished' && (
						<>
							<div className={styles.finishedMessage}>{progress.message}</div>
							<button
								className={`${styles.actionButton} ${styles.actionInfoButton}`}
								onClick={() =>
									window.open('https://crm.staging.svoimax.ru', '_blank')
								}
							>
								Перейти в кабинет
							</button>
						</>
					)}
				</div>
			)}
			<div className={styles.inputContainer}>
				<textarea
					ref={textareaRef}
					className={styles.input}
					value={inputValue}
					onChange={e => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder='Введите сообщение'
					rows={1}
				/>
				<button
					className={styles.sendButton}
					onClick={() => handleSendMessage(inputValue)}
				>
					Отправить
				</button>
			</div>
			<div>
				<button className={styles.clearButton} onClick={handleClear}>
					Очистить чат и начать сначала
				</button>
			</div>
		</div>
	)
}

export default BusinessChat
